<script setup lang="ts">
import { CallIcon, FaxIcon, PhoneIcon } from '@blpde/nx-icons';
import { computed, useTemplateRef, type Component, type InputHTMLAttributes } from 'vue';
import type { BaseTextInputProps, InlineIconProps } from './internal';
import NxInput from './NxInput.vue';

// A phone-number field: renders and behaves like `NxTextInput`, but bakes in a
// trailing icon that dials the number as a `tel:` link. `variant` picks the icon
// and default label; all three dial identically, since a mobile or fax number is
// just a dialable phone number (RFC 3966). The stored value is never rewritten —
// the field keeps its formatting while only a leading `+` and the digits are
// dialled.
type TelVariant = 'mobile' | 'phone' | 'fax';

// Icon and default label for each variant. `Record<TelVariant, …>` makes both
// mandatory — adding a variant without an icon or label won't compile. Glyphs:
// mobile = smartphone, phone = handset, fax = fax machine.
const VARIANT_ICON: Record<TelVariant, Component> = {
  mobile: PhoneIcon,
  phone: CallIcon,
  fax: FaxIcon,
};
const VARIANT_LABEL: Record<TelVariant, string> = {
  mobile: 'Mobile',
  phone: 'Call',
  fax: 'Fax',
};

const props = withDefaults(
  defineProps<
    BaseTextInputProps & {
      /** Kind of number — selects the trailing icon and default label. */
      variant?: TelVariant;
      /** Accessible label / tooltip for the trailing icon button. Defaults to
       * "Mobile" / "Call" / "Fax", based on `variant`. */
      openLabel?: string;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>
  >(),
  { variant: 'phone', openLabel: undefined }
);

const iconComponent = computed(() => VARIANT_ICON[props.variant]);
const iconLabel = computed(() => props.openLabel ?? VARIANT_LABEL[props.variant]);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

// A phone number is made of digits and the grouping characters `/ - ( )`, plus
// two characters that are only valid in a certain position:
//   `+` — the country prefix, so only ahead of the number itself and therefore
//         only once. `Tel. +49 30` keeps its plus, `49+30` loses it.
//   ` ` — separates number groups, so a single one needs a number character to
//         its left: leading whitespace drops out, and a run of spaces collapses
//         to the one separator it means. A single trailing space survives — the
//         user is typing towards the next group.
const NON_TEL_CHAR = /[^\d /()-]/g;
const SPACE_RUN = / {2,}/g;
const LEADING_PLUS = /^[^\d/()-]*\+/;
function sanitizeTel(raw: string): string {
  const plus = LEADING_PLUS.test(raw) ? '+' : '';
  return plus + raw.replace(NON_TEL_CHAR, '').replace(SPACE_RUN, ' ').trimStart();
}

// Filter on the way in, so the model never holds anything but number
// characters. NxInput binds `v-model` to the native input, so this sits in
// between: the raw DOM value lands here and only the filtered one reaches
// `valueModel`.
const inputModel = computed<string>({
  get: () => valueModel.value,
  set: (raw) => {
    valueModel.value = sanitizeTel(raw);
  },
});

// The element still shows the raw text at this point — `input` fires after the
// browser inserted it. Filtering through the model alone would not clean it up:
// when the filtered value equals the current one, Vue re-renders nothing and the
// rejected character stays visible. So rewrite the element and put the caret
// back by however many characters were dropped ahead of it, instead of letting
// it snap to the end.
function filterInput(event: Event) {
  const el = event.target as HTMLInputElement;
  const filtered = sanitizeTel(el.value);
  if (filtered === el.value) return;
  const caret = el.selectionStart ?? el.value.length;
  const filteredCaret = sanitizeTel(el.value.slice(0, caret)).length;
  el.value = filtered;
  el.setSelectionRange(filteredCaret, filteredCaret);
}

const hasValue = computed(() => (valueModel.value ?? '').trim().length > 0);

function openTel() {
  const trimmed = (valueModel.value ?? '').trim();
  if (!trimmed) return;
  // Dial string (RFC 3966): keep a leading `+` and digits only, dropping spaces,
  // parens and dashes. The displayed value keeps its formatting.
  const dial = trimmed.replace(/[^\d+]/g, '');
  if (!dial) return;
  window.location.href = `tel:${dial}`;
}

// The trailing icon. With a value it is a clickable, brand-coloured button; when
// empty we omit `onClick`, so NxInputWrapper renders an inert, greyed <span> —
// visible but not clickable.
const telIcons = computed<InlineIconProps[]>(() => [
  {
    component: iconComponent.value,
    align: 'end',
    filled: true,
    color: hasValue.value ? 'var(--nxt-brand-bg)' : 'var(--nxt-fg-muted)',
    label: iconLabel.value,
    onClick: hasValue.value ? () => openTel() : undefined,
    deco: false,
  },
]);

const inputRef = useTemplateRef('input-element');
const exposeRef = computed(() => {
  return inputRef.value?.inputElement;
});
defineExpose({
  inputElement: exposeRef,
});
</script>

<template>
  <NxInput
    v-if="valueModelModifiers.lazy"
    :id="$props.id"
    ref="input-element"
    v-model.lazy="inputModel"
    type="text"
    inputmode="tel"
    :icons="telIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
    @input="filterInput"
  />
  <NxInput
    v-else
    :id="$props.id"
    ref="input-element"
    v-model="inputModel"
    type="text"
    inputmode="tel"
    :icons="telIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
    @input="filterInput"
  />
</template>
